/**
 * Credential detection patterns for the replay pipeline.
 *
 * Two-layer defense against credential leaks in .fern/replay.lock:
 *   Layer 1 — file-level: SENSITIVE_FILE_PATTERNS blocks entire files (.env, .pem, etc.)
 *   Layer 2 — content-level: CREDENTIAL_PATTERNS catches inline secrets in any file
 *
 * These patterns mirror the invariant assertions in __tests__/invariants/i4-credential-containment.ts.
 * If they diverge, the invariant tests independently catch real leaks.
 */

export interface CredentialPattern {
    name: string;
    re: RegExp;
}

export const CREDENTIAL_PATTERNS: ReadonlyArray<CredentialPattern> = [
    { name: "PRIVATE KEY block", re: /-----BEGIN [A-Z ]*PRIVATE KEY-----/ },
    { name: "OPENSSH key", re: /-----BEGIN OPENSSH PRIVATE KEY-----/ },
    { name: "RSA key", re: /-----BEGIN RSA PRIVATE KEY-----/ },
    { name: "CERTIFICATE", re: /-----BEGIN CERTIFICATE-----/ },
    { name: "TOKEN=", re: /\bTOKEN\s*=\s*["']?[A-Za-z0-9._\-+/=]{10,}/ },
    { name: "api_key=", re: /\bapi[_-]?key\s*=\s*["']?[A-Za-z0-9._\-+/=]{10,}/i },
    { name: "password=", re: /\bpassword\s*=\s*["']?[^\s"']{6,}/i },
    { name: "AWS_SECRET_ACCESS_KEY", re: /AWS_SECRET_ACCESS_KEY/ },
    { name: "JWT (eyJ...)", re: /\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/ },
];

export const SENSITIVE_FILE_PATTERNS: ReadonlyArray<RegExp> = [
    /(^|\/)\.env(\.[^/]+)?$/,
    /\.pem$/,
    /\.key$/,
    /\.pkce_state\.json$/,
    /(^|\/)id_rsa$/,
    /(^|\/)id_ed25519$/,
];

/** Check if a file path matches any sensitive file pattern. */
export function isSensitiveFile(filePath: string): boolean {
    return SENSITIVE_FILE_PATTERNS.some((re) => re.test(filePath));
}

/** Scan content for credential patterns. Returns matched pattern names. */
export function scanForCredentials(content: string): string[] {
    const matches: string[] = [];
    for (const { name, re } of CREDENTIAL_PATTERNS) {
        if (re.test(content)) {
            matches.push(name);
        }
    }
    return matches;
}
